import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { ChefWillBadge } from "@/components/ChefWillBadge";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/hooks/useCatalog";
import { gbp } from "@/lib/api";

export const FlapjackSpotlight = () => {
  const ref = useRef(null);
  const { add, setOpen } = useCart();
  const { products } = useCatalog();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["-6%", "14%"]);
  const rot = useTransform(scrollYProgress, [0, 1], [-10, 10]);

  const quickAdd = (p) => {
    add(p.lookup_key);
    toast.success(`${p.name} added`, { action: { label: "View basket", onClick: () => setOpen(true) } });
  };

  return (
    <section ref={ref} className="fj-dots relative overflow-hidden py-24 text-[#3B1220] sm:py-32" data-testid="flapjack-spotlight">
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="relative h-[420px] sm:h-[520px] lg:col-span-6">
          <div className="absolute left-1/2 top-1/2 h-[90%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,#fff_0%,#FFC2D3_55%,transparent_72%)]" />
          <motion.div style={{ y: y2, rotate: rot }} className="absolute right-0 top-0 h-[76%] sm:right-6">
            <img src="/images/fj_waffle_pouch.webp" alt="Flapjack Waffle Mix pouch" className="h-full w-auto rotate-6 object-contain drop-shadow-[0_30px_40px_rgba(59,18,32,0.35)]" />
          </motion.div>
          <motion.div style={{ y: y1 }} className="absolute bottom-0 left-0 h-[84%] sm:left-6">
            <img src="/images/fj_pancake_pouch.webp" alt="Flapjack Pancake Mix pouch" className="h-full w-auto -rotate-6 object-contain drop-shadow-[0_40px_50px_rgba(59,18,32,0.4)]" />
          </motion.div>
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <ChefWillBadge id="cw-home" className="h-28 w-28 sm:h-36 sm:w-36" />
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="eyebrow text-[#B83560]">04 — Our Flapjack brand</p>
          <motion.img
            src="/images/flapjack_logo.png"
            alt="Flapjack"
            initial={{ scale: 0.7, rotate: -6, opacity: 0 }}
            whileInView={{ scale: 1, rotate: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ type: "spring", stiffness: 140, damping: 12 }}
            className="mt-5 w-full max-w-[460px]"
          />
          <p className="font-retro mt-6 text-2xl text-[#D0466F] sm:text-3xl">Classic American style, made at home in minutes.</p>
          <p className="mt-4 max-w-lg text-[#5A2A3A]">
            Chef Will's pre-measured pancake and waffle mixes. Just add milk for tall, fluffy stacks and crisp, golden waffles, at home, in a café or across a full catering service.
          </p>
          <div className="mt-8 space-y-3">
            {products.map((p) => (
              <div key={p.lookup_key} className="flex items-center justify-between gap-4 rounded-2xl bg-white/80 p-4 backdrop-blur" data-testid={`spotlight-product-${p.lookup_key}`}>
                <div className="flex items-center gap-4">
                  <img src={p.image} alt="" className="h-14 w-14 rounded-xl bg-[#FFD8E2] object-contain p-1" />
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-sm text-[#7A3B52]">{gbp(p.amount)}</p>
                  </div>
                </div>
                <button onClick={() => quickAdd(p)} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#D0466F] text-white transition-transform hover:scale-110" data-testid={`spotlight-add-${p.lookup_key}`} aria-label={`Add ${p.name}`}>
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
          <Link to="/flapjack" className="btn-ghost mt-8 text-[#B83560] hover:bg-white/60" data-testid="spotlight-shop-link">
            Discover Flapjack <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
